/**
 * landing/live-ticker LIVE ticker island.
 *
 * Mount: `<div data-mount="live-ticker" data-ticker-state="empty">
 *           <div data-ticker-track></div>
 *           <div data-ticker-default>🚪 첫 탈출자가 되어주세요</div>
 *         </div>`
 *
 * 흐름:
 *   - mount 시 GET /api/recent-exits → 3 상태 중 하나로 전이 (loading/live/empty).
 *   - items > 0 → state="live", track 노출 + default 숨김 (item 두 번 복제, CSS
 *     animation 으로 무한 스크롤. animation 자체는 SSG CSS).
 *   - items = 0 → state="empty", track 숨김 + default 메시지 노출.
 *   - 30초 polling. 데이터 변경 시 부드럽게 re-render (변화 없으면 skip).
 *   - 응답에 anon_id 가 들어있으면 silent skip (security guard).
 *   - DOM only — fetch 1 (mount) + 30 s polling.
 */

type Item = { nickname: string; service_name: string; relative_time: string };
type Response = { items: Item[] };

const POLL_MS = 30_000;
const ENDPOINT = "/api/recent-exits";

function itemKey(it: Item): string {
  return `${it.nickname}|${it.service_name}|${it.relative_time}`;
}

function renderItems(track: HTMLElement, items: Item[]): void {
  track.innerHTML = "";
  // 무한 스크롤 — 같은 list 두 번 (CSS animation 이 50% 시점에 reset).
  for (let pass = 0; pass < 2; pass++) {
    for (const it of items) {
      const span = document.createElement("span");
      span.dataset.tickerItem = "";
      span.dataset.itemKey = itemKey(it);
      span.className = "inline-flex items-center gap-2 px-3 text-xs";
      span.innerHTML = `<span class="font-medium text-slate-900"></span><span class="text-slate-500"></span><span class="text-emerald-600">→ EXITED</span><span class="text-slate-400"></span>`;
      const [nick, svc, , t] = span.children;
      if (nick) nick.textContent = it.nickname;
      if (svc) svc.textContent = it.service_name;
      if (t) t.textContent = it.relative_time;
      track.appendChild(span);
    }
  }
}

async function fetchOnce(): Promise<Item[] | null> {
  let res: globalThis.Response;
  try {
    res = await fetch(ENDPOINT);
  } catch {
    return null;
  }
  if (!res.ok) return null;
  let data: Response;
  try {
    data = (await res.json()) as Response;
  } catch {
    return null;
  }
  if (!Array.isArray(data?.items)) return null;
  // 보안 가드 — anon_id key 가 응답에 들어있으면 기록 무시.
  if (data.items.some((it) => "anon_id" in (it as Record<string, unknown>))) return null;
  return data.items;
}

function setState(
  root: HTMLElement,
  track: HTMLElement,
  defaultEl: HTMLElement | null,
  state: "loading" | "live" | "empty",
): void {
  root.dataset.tickerState = state;
  if (state === "live") {
    track.hidden = false;
    if (defaultEl) defaultEl.hidden = true;
  } else {
    track.hidden = true;
    if (defaultEl) defaultEl.hidden = false;
  }
}

export function mountLiveTicker(root: HTMLElement): void {
  const track = root.querySelector<HTMLElement>("[data-ticker-track]");
  if (!track) return;
  const defaultEl = root.querySelector<HTMLElement>("[data-ticker-default]");

  let lastKeys = new Set<string>();

  const tick = async (): Promise<void> => {
    const items = await fetchOnce();
    if (items === null) return; // 5xx / network — 현 state 유지
    if (items.length === 0) {
      track.innerHTML = "";
      lastKeys = new Set();
      setState(root, track, defaultEl, "empty");
      return;
    }
    const newKeys = new Set(items.map(itemKey));
    // 변화 없으면 skip — re-render 비용 X.
    let changed = newKeys.size !== lastKeys.size;
    if (!changed) {
      for (const k of newKeys) {
        if (!lastKeys.has(k)) {
          changed = true;
          break;
        }
      }
    }
    if (changed) {
      renderItems(track, items);
      lastKeys = newKeys;
    }
    setState(root, track, defaultEl, "live");
  };

  void tick();
  setInterval(() => {
    void tick();
  }, POLL_MS);
}
